import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { DELETE_CLIENT_INCOME, EDIT_CLIENT_INCOME } from "../../constants";
import { CardLayout, DeleteModal, MTable } from "../v3";
import { ProjectIncomeTableColumns } from "../../pages/project/constants";
import { FinanceService } from "../../services";
import { get_date } from "../generalComponets";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const ProjectIncome = () => {
  const [incomes, setIncomes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "project-income",
    defaults: { sortModel: [{ field: "date", sort: "desc" }] },
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null });

  const canEditIncome = useHasPermission(EDIT_CLIENT_INCOME);
  const canDeleteIncome = useHasPermission(DELETE_CLIENT_INCOME);

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const fetchData = useCallback(
    async (incomeFilters = {}) => {
      setLoading(true);

      try {
        const incomeResponse = await FinanceService.getAllIncome({
          ...incomeFilters,
          projectId: id,
        });

        setIncomes(incomeResponse?.data?.rows);
        setTotalCount(incomeResponse?.data?.count || 0);
      } catch (error) {
        toast.error("Failed to fetch income statement");
        console.error("🚀 ~ fetchData error:", error);
      } finally {
        setLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      description: searchText,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [fetchData, sortModel, paginationState, searchText, dateRange]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Use selected boundaries instead of fixed offsets for project income statements.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(ProjectIncomeTableColumns());
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Income" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Income",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params, event) => {
    navigate(`/client/receipt/${params.row.id}`);
  };

  const openDeleteModal = (income) => {
    setDeleteModal({ isOpen: true, recordId: income.id });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, recordId: null });
  };

  const handleDelete = async () => {
    try {
      await FinanceService.deleteIncome(deleteModal.recordId);
      toast.success("Income deleted successfully");
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        description: searchText,
        fromDate: dateRange?.startDate,
        toDate: dateRange?.endDate,
      });
    } catch (error) {
      toast.error("Failed to delete income");
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = (incomeData, actionType) => {
    switch (actionType) {
      case "edit":
        navigate(`/edit-income/${incomeData.id}`);
        break;
      case "delete":
        openDeleteModal(incomeData);
        break;
      default:
        return null;
    }
  };

  const columnHeaders = ProjectIncomeTableColumns(handleActions, canEditIncome, canDeleteIncome);

  const rows = incomes.map((income, index) => {
    return {
      id: income.id,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      date: get_date(income.date),
      description: income.description,
      amount: income.amount,
    };
  });

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <CardLayout>
      <MTable
        // Search data
        placeholder="Search Income"
        searchText={searchText}
        onSearchChange={handleSearchChange}
        showSearchBar
        // Date range data
        showDateRangePicker
        dateRange={dateRange}
        onDateRangeChange={handleDateRangeChange}
        // Export data
        showExportButton
        isPdfGenerating={isPdfGenerating}
        onExportPdfClick={handleDownloadPdfClick}
        // Table data
        columnHeaders={columnHeaders}
        tableRows={rows}
        rowCount={totalCount || 0}
        loading={loading}
        sortModel={sortModel}
        onSortChange={setSortModel}
        defaultSortModel={DEFAULT_SORT}
        paginationModel={paginationState}
        onPaginationChange={handlePaginationChange}
        onRowClick={onViewClick}
      />
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this income?"
      />
    </CardLayout>
  );
};
